// src/components/fields/DateOfBirthField.jsx
//
// Controlled date-of-birth field. Adapts the follow-up screen's personal-details
// picker (FollowUpV2 → sections/@dashboard/general/app/PersonalDetails.jsx, MUI
// DesktopDatePicker, dd/MM/yyyy, future dates disabled) so booking flows use the
// same control instead of a browser-native <input type="date">.
//
// Contract mirrors the native control it replaces:
//   value     ISO date-only string ('YYYY-MM-DD') or ''.
//   onChange  called with the next ISO date-only string ('' when cleared).
//
// Commit semantics: typed or popover changes are held as a local draft.
// `onChange` fires only for a completed user change — a day chosen in the
// calendar (MUI `onAccept`), or a complete valid typed date on blur/Enter.
// Partial or impossible typed dates stay visible as an error and are never
// emitted; dismissing the calendar (Escape / click-away) emits nothing and
// drops the draft. Note MUI v6 routes a dismiss through `onAccept` with the
// value the calendar was showing, so `onAccept` is NOT used as the commit
// signal; the day slot's click (mouse or keyboard Enter on the focused day)
// is, and the committed date is read from the clicked day itself (its
// `data-timestamp`), never from a remembered draft — MUI does not re-publish
// `onChange` when the clicked day equals its last published value, so a
// draft discarded by Escape could otherwise be committed on the next click.
// This matters for consumers that autosave on change (e.g. the WhatsApp
// booking panel).
// No timezone conversion is applied (see src/utils/dobDate.js).
import { useState } from 'react';
import PropTypes from 'prop-types';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { isValid } from 'date-fns';
import { DOB_DISPLAY_FORMAT, formatDob, parseDob } from '../../utils/dobDate';

const INVALID_DOB_MESSAGE = 'Enter a valid date of birth';
const FUTURE_DOB_MESSAGE = 'Date of birth cannot be in the future';

export default function DateOfBirthField({
    value,
    onChange,
    label,
    required,
    disabled,
    error,
    helperText,
    size,
    fullWidth,
    placeholder,
    sx,
    inputProps,
}) {
    // `undefined` = no open draft; the picker shows the committed `value`.
    const [draft, setDraft] = useState(undefined);
    // A malformed/partial entry is kept visible as an error until corrected;
    // it is never silently emitted as an accepted empty DOB.
    const [entryError, setEntryError] = useState(null);

    const commit = (nextDate) => {
        setDraft(undefined);
        if (nextDate === null) {
            setEntryError(null);
            onChange('');
            return;
        }
        if (!isValid(nextDate)) {
            setEntryError(INVALID_DOB_MESSAGE);
            return;
        }
        if (nextDate > new Date()) {
            setEntryError(FUTURE_DOB_MESSAGE);
            return;
        }
        setEntryError(null);
        onChange(formatDob(nextDate));
    };

    // Field typing and calendar navigation land here; nothing is committed yet.
    const handleDraftChange = (nextDate) => {
        setDraft(nextDate);
        if (entryError && (nextDate === null || isValid(nextDate))) setEntryError(null);
    };

    // A day was chosen in the calendar (mouse click, or Enter/Space on the
    // focused day button). PickersDay renders the day's local-midnight epoch as
    // `data-timestamp`; that is the authoritative choice. A click on the day
    // already committed changes nothing and emits nothing.
    const handleDayClick = (event) => {
        const timestamp = Number(event.currentTarget?.dataset?.timestamp);
        if (!Number.isFinite(timestamp)) return;
        const chosen = new Date(timestamp);
        if (formatDob(chosen) === value) {
            setDraft(undefined);
            setEntryError(null);
            return;
        }
        commit(chosen);
    };

    // Popover closed (day chosen, Escape or click-away): drop any uncommitted
    // draft so the field shows the committed value again.
    const handleClose = () => setDraft(undefined);

    // Typed entry never reaches a calendar click; treat leaving the field (or
    // Enter) as completing the entry. Partial text stays as a draft with an error.
    const commitDraftIfComplete = () => {
        if (draft === undefined) return;
        if (draft !== null && !isValid(draft)) {
            setEntryError(INVALID_DOB_MESSAGE);
            return;
        }
        commit(draft);
    };

    return (
        <DesktopDatePicker
            orientation="landscape"
            label={label}
            value={draft === undefined ? parseDob(value) : draft}
            format={DOB_DISPLAY_FORMAT}
            maxDate={new Date()}
            disabled={disabled}
            onChange={handleDraftChange}
            onClose={handleClose}
            slotProps={{
                day: { onClick: handleDayClick },
                textField: {
                    size,
                    required,
                    fullWidth,
                    placeholder,
                    sx,
                    error: Boolean(entryError) || !!error,
                    helperText: entryError || helperText,
                    onBlur: commitDraftIfComplete,
                    onKeyDown: (event) => { if (event.key === 'Enter') commitDraftIfComplete(); },
                    inputProps: { 'aria-label': label || 'Date of birth', ...inputProps },
                },
            }}
        />
    );
}

DateOfBirthField.propTypes = {
    value: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    label: PropTypes.string,
    required: PropTypes.bool,
    disabled: PropTypes.bool,
    error: PropTypes.bool,
    helperText: PropTypes.node,
    size: PropTypes.oneOf(['small', 'medium']),
    fullWidth: PropTypes.bool,
    placeholder: PropTypes.string,
    sx: PropTypes.oneOfType([PropTypes.object, PropTypes.array, PropTypes.func]),
    inputProps: PropTypes.object,
};

DateOfBirthField.defaultProps = {
    value: '',
    label: 'Date of birth',
    required: false,
    disabled: false,
    error: false,
    helperText: '',
    size: 'medium',
    fullWidth: false,
    placeholder: undefined,
    sx: undefined,
    inputProps: undefined,
};
